/**
 * Deep Star Cathedral Visualizer — Dense cinematic starfield with layered depth
 *
 * A "calm maximalism" audio visualizer: visually rich and immersive but sleep-safe
 * (slow motion, no strobes, no harsh brightness). Seven+ distinct visual layers
 * with parallax depth, multi-palette color system, dust clouds, god-ray light shafts,
 * color wash, and slow comets.
 *
 * Differentiates from starfield-drift by being far more layered, with a sophisticated
 * multi-palette color system instead of a single color temperature slider.
 *
 * Rendering layers (bottom to top):
 * 1. Trail fade — offscreen canvas composited at reduced opacity over fresh background
 * 2. Background gradient + vignette — radial deep-space gradient with edge darkening
 *    (drawn OUTSIDE trail composite to prevent accumulation)
 * 3. Far stars — tiny specks (fillRect), very slow drift, sine-based twinkle
 * 4. Mid stars — small circles (arc), moderate drift, optional shadowBlur glow
 * 5. Dust clouds — radial gradient blobs + stroked wisps, slow drift, bass-modulated alpha
 * 6. Light shafts / god rays — tall translucent gradient rectangles, bass-boosted
 * 7. Near bokeh stars — large soft circles with radial gradient, fastest drift
 * 8. Color wash — very subtle rotating linear gradient overlay
 * 9. Comets — slow streaks from object pool, spawned by energy rise detector
 *
 * Audio mapping (all delta-clamped, dampened by calmness):
 * - Bass → shaft intensity boost (+40% max), dust alpha boost (+25% max)
 * - Mids → parallax drift speed multiplier (0.8x to 1.5x, clamped)
 * - Treble → twinkle amplitude multiplier (0.5x to 1.5x)
 * - Overall energy → comet spawning via integrator (no burst spawns)
 * - No beat flash effects — this is calmer than starfield-drift
 *
 * Uses offscreen canvas trail technique that respects the global background
 * controller (solid, gradient, pattern, or image backgrounds).
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';
import { mapFrequencyData, smoothAmplitudes } from '../lib/audio-utils.js';

// Metadata for manifest generation
export const metadata = {
  name: 'deep-star-cathedral',
  displayName: 'Deep Star Cathedral',
  contextType: '2d',
};

// ─── Pool Size Constants ─────────────────────────────────────────────────
const MAX_STARS_FAR = 600;
const MAX_STARS_MID = 400;
const MAX_STARS_NEAR = 60;
const MAX_DUST = 80;
const MAX_SHAFTS = 20;
const MAX_COMETS = 10;

// Total star pool: far + mid + near stored in one flat array
const MAX_STARS = MAX_STARS_FAR + MAX_STARS_MID + MAX_STARS_NEAR;

// Star layer definitions: speed and size multipliers for parallax depth
const STAR_LAYERS = {
  FAR:  { speed: 0.12, sizeMin: 0.4, sizeMax: 1.0 },
  MID:  { speed: 0.35, sizeMin: 0.8, sizeMax: 1.8 },
  NEAR: { speed: 0.80, sizeMin: 2.0, sizeMax: 4.5 },
};

// Dust cloud scale classes: large diffuse (40%), medium (25%), small bright (20%), stroked wisps (15%)
const DUST_CLASSES = [
  { weight: 0.40, radiusMin: 0.06, radiusMax: 0.14, alphaBase: 0.015, stroked: false },
  { weight: 0.65, radiusMin: 0.03, radiusMax: 0.07, alphaBase: 0.025, stroked: false },
  { weight: 0.85, radiusMin: 0.015, radiusMax: 0.035, alphaBase: 0.04, stroked: false },
  { weight: 1.00, radiusMin: 0.02, radiusMax: 0.06, alphaBase: 0.03, stroked: true },
];

// Shaft brightness classes: dim (50%), medium (35%), bright (15%)
const SHAFT_CLASSES = [
  { weight: 0.50, widthMin: 2, widthMax: 6, alphaBase: 0.02 },
  { weight: 0.85, widthMin: 4, widthMax: 10, alphaBase: 0.035 },
  { weight: 1.00, widthMin: 8, widthMax: 18, alphaBase: 0.05 },
];

// ─── Palette Definitions (muted, harmonious deep-space colors) ───────────
const PALETTES = [
  // Deep Blue
  ['#0a1628', '#122040', '#1a3058', '#224070', '#2a5088', '#3268a0'],
  // Purple Nebula
  ['#1a0a28', '#281440', '#381e58', '#482870', '#583288', '#6840a0'],
  // Warm Gold
  ['#281a0a', '#402814', '#58381e', '#704828', '#885832', '#a06840'],
  // Cool Cyan
  ['#0a2028', '#142830', '#1e3840', '#284850', '#325860', '#406878'],
  // Rose Red
  ['#280a14', '#40141e', '#581e28', '#702832', '#88323c', '#a04048'],
  // Teal Green
  ['#0a281a', '#143024', '#1e4030', '#28503c', '#326048', '#407858'],
];

// Pre-parsed palette RGB values (avoids per-frame hex parsing in hot loops)
const PALETTES_RGB = PALETTES.map(pal =>
  pal.map(hex => [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ])
);

const TAU = Math.PI * 2;

class DeepStarCathedral extends Base2DVisualizer {
  static name = 'deep-star-cathedral';
  static displayName = 'Deep Star Cathedral';

  static getConfigUI() {
    return [
      { type: 'section', label: 'Stars' },
      { type: 'range', id: 'starsFar', label: 'Far Stars', min: 100, max: 600, default: 400 },
      { type: 'range', id: 'starsMid', label: 'Mid Stars', min: 50, max: 400, default: 250 },
      { type: 'range', id: 'starsNear', label: 'Near Bokeh Stars', min: 5, max: 60, default: 30 },
      { type: 'range', id: 'twinkleStrength', label: 'Twinkle Strength', min: 0, max: 100, default: 50, scale: 0.01 },
      { type: 'range', id: 'twinklePeriod', label: 'Twinkle Period', min: 10, max: 100, default: 40, scale: 0.01 },

      { type: 'section', label: 'Atmosphere' },
      { type: 'range', id: 'dustCount', label: 'Dust Clouds', min: 0, max: 80, default: 50 },
      { type: 'range', id: 'shaftCount', label: 'Light Shafts', min: 0, max: 20, default: 12 },
      { type: 'range', id: 'cometRate', label: 'Comet Rate', min: 0, max: 100, default: 30, scale: 0.01 },
      { type: 'range', id: 'paletteRichness', label: 'Palette Richness', min: 1, max: 6, default: 4 },
      { type: 'range', id: 'hueDriftSpeed', label: 'Hue Drift Speed', min: 0, max: 100, default: 20, scale: 0.01 },

      { type: 'section', label: 'Mood' },
      { type: 'range', id: 'calmness', label: 'Calmness', min: 10, max: 100, default: 60, scale: 0.01 },
      { type: 'range', id: 'trailAlpha', label: 'Trail Fade', min: 1, max: 15, default: 6, scale: 0.01 },
      { type: 'range', id: 'glowIntensity', label: 'Glow Intensity', min: 0, max: 30, default: 10 },
      { type: 'range', id: 'highlightCeiling', label: 'Highlight Ceiling', min: 20, max: 100, default: 70, scale: 0.01 },
      { type: 'checkbox', id: 'safeMode', label: 'Safe Mode (lower brightness)', default: false },

      { type: 'section', label: 'Audio' },
      { type: 'range', id: 'smoothing', label: 'Audio Smoothing', min: 0, max: 95, default: 85, scale: 0.01 },
    ];
  }

  getDefaultConfig() {
    return {};
  }

  getElementCount() {
    return 64;
  }

  // ═══════════════════════════════════════════════
  // Initialization
  // ═══════════════════════════════════════════════

  init() {
    this.time = 0;
    this.isFirstFrame = true;

    // Trail offscreen canvas
    this._trailCanvas = null;
    this._trailCtx = null;

    // Vignette cache (rebuilt on resize only)
    this._vignetteCanvas = null;
    this._vigW = 0;
    this._vigH = 0;

    // Smoothed audio values (per-frame delta-clamped)
    this._smoothedBass = 0;
    this._smoothedMids = 0;
    this._smoothedTreble = 0;
    this._smoothedEnergy = 0;

    // Energy rise detector (integrator for comet spawning)
    this._energyIntegrator = 0;
    this._energyThreshold = 0.35;
    this._lastEnergy = 0;

    // Active palette indices
    this._activePalettes = [];
    this._paletteHueOffset = 0;
    this._selectPalettes();

    // Reusable RGB output array (avoids per-call allocation in hot loops)
    this._rgb = [0, 0, 0];

    // Color wash rotation angle
    this._washAngle = 0;

    // Initialize all pools
    this._initStarPool();
    this._initDustPool();
    this._initShaftPool();
    this._initCometPool();

    // Activate initial elements
    this._activateStars();
    this._activateDust();
    this._activateShafts();
  }

  // ═══════════════════════════════════════════════
  // Pool Initialization
  // ═══════════════════════════════════════════════

  _initStarPool() {
    this._stars = new Array(MAX_STARS);
    for (let i = 0; i < MAX_STARS; i++) {
      this._stars[i] = {
        active: false,
        layer: 0,       // 0=far, 1=mid, 2=near
        x: 0, y: 0,
        baseSize: 0,
        phase: 0,
        twinkleSpeed: 0,
        brightness: 0,
        paletteT: 0,    // 0-1 for palette color assignment
      };
    }
  }

  _initDustPool() {
    this._dust = new Array(MAX_DUST);
    for (let i = 0; i < MAX_DUST; i++) {
      this._dust[i] = {
        active: false,
        scaleClass: 0,   // index into DUST_CLASSES
        x: 0, y: 0,      // normalized 0-1
        radius: 0,        // normalized
        phase: 0,
        breathSpeed: 0,
        paletteT: 0,
        driftVx: 0,
        driftVy: 0,
      };
    }
  }

  _initShaftPool() {
    this._shafts = new Array(MAX_SHAFTS);
    for (let i = 0; i < MAX_SHAFTS; i++) {
      this._shafts[i] = {
        active: false,
        brightnessClass: 0,
        x: 0,            // normalized 0-1
        width: 0,
        height: 0,        // normalized 0-1
        phase: 0,
        swaySpeed: 0,
        paletteT: 0,
        driftVx: 0,
      };
    }
  }

  _initCometPool() {
    this._comets = new Array(MAX_COMETS);
    for (let i = 0; i < MAX_COMETS; i++) {
      this._comets[i] = {
        active: false,
        x: 0, y: 0,
        vx: 0, vy: 0,
        life: 0,
        maxLife: 0,
        length: 0,
        paletteT: 0,
      };
    }
    this._cometNextIdx = 0;
  }

  // ═══════════════════════════════════════════════
  // Pool Activation
  // ═══════════════════════════════════════════════

  _activateStars() {
    const w = this.canvas.width || 1920;
    const h = this.canvas.height || 1080;
    const { starsFar, starsMid, starsNear } = this.config;

    let idx = 0;

    // Far stars
    for (let i = 0; i < MAX_STARS_FAR; i++, idx++) {
      const star = this._stars[idx];
      if (i < starsFar) {
        star.active = true;
        star.layer = 0;
        star.x = Math.random() * w;
        star.y = Math.random() * h;
        star.baseSize = STAR_LAYERS.FAR.sizeMin + Math.random() * (STAR_LAYERS.FAR.sizeMax - STAR_LAYERS.FAR.sizeMin);
        star.phase = Math.random() * TAU;
        star.twinkleSpeed = 0.3 + Math.random() * 1.2;
        star.brightness = 0.3 + Math.random() * 0.7;
        star.paletteT = Math.random();
      } else {
        star.active = false;
      }
    }

    // Mid stars
    for (let i = 0; i < MAX_STARS_MID; i++, idx++) {
      const star = this._stars[idx];
      if (i < starsMid) {
        star.active = true;
        star.layer = 1;
        star.x = Math.random() * w;
        star.y = Math.random() * h;
        star.baseSize = STAR_LAYERS.MID.sizeMin + Math.random() * (STAR_LAYERS.MID.sizeMax - STAR_LAYERS.MID.sizeMin);
        star.phase = Math.random() * TAU;
        star.twinkleSpeed = 0.2 + Math.random() * 1.0;
        star.brightness = 0.4 + Math.random() * 0.6;
        star.paletteT = Math.random();
      } else {
        star.active = false;
      }
    }

    // Near bokeh stars
    for (let i = 0; i < MAX_STARS_NEAR; i++, idx++) {
      const star = this._stars[idx];
      if (i < starsNear) {
        star.active = true;
        star.layer = 2;
        star.x = Math.random() * w;
        star.y = Math.random() * h;
        star.baseSize = STAR_LAYERS.NEAR.sizeMin + Math.random() * (STAR_LAYERS.NEAR.sizeMax - STAR_LAYERS.NEAR.sizeMin);
        star.phase = Math.random() * TAU;
        star.twinkleSpeed = 0.1 + Math.random() * 0.6;
        star.brightness = 0.5 + Math.random() * 0.5;
        star.paletteT = Math.random();
      } else {
        star.active = false;
      }
    }
  }

  _activateDust() {
    const count = Math.min(this.config.dustCount, MAX_DUST);
    for (let i = 0; i < MAX_DUST; i++) {
      const d = this._dust[i];
      if (i < count) {
        d.active = true;
        // Assign scale class by weighted random
        const roll = Math.random();
        let classIdx = 0;
        for (let c = 0; c < DUST_CLASSES.length; c++) {
          if (roll < DUST_CLASSES[c].weight) { classIdx = c; break; }
        }
        d.scaleClass = classIdx;
        const cls = DUST_CLASSES[classIdx];
        d.x = Math.random();
        d.y = Math.random();
        d.radius = cls.radiusMin + Math.random() * (cls.radiusMax - cls.radiusMin);
        d.phase = Math.random() * TAU;
        d.breathSpeed = 0.1 + Math.random() * 0.3;
        d.paletteT = Math.random();
        d.driftVx = (Math.random() - 0.5) * 0.002;
        d.driftVy = (Math.random() - 0.5) * 0.001;
      } else {
        d.active = false;
      }
    }
  }

  _activateShafts() {
    const count = Math.min(this.config.shaftCount, MAX_SHAFTS);
    for (let i = 0; i < MAX_SHAFTS; i++) {
      const s = this._shafts[i];
      if (i < count) {
        s.active = true;
        const roll = Math.random();
        let classIdx = 0;
        for (let c = 0; c < SHAFT_CLASSES.length; c++) {
          if (roll < SHAFT_CLASSES[c].weight) { classIdx = c; break; }
        }
        s.brightnessClass = classIdx;
        const cls = SHAFT_CLASSES[classIdx];
        s.x = Math.random();
        s.width = cls.widthMin + Math.random() * (cls.widthMax - cls.widthMin);
        s.height = 0.3 + Math.random() * 0.5;
        s.phase = Math.random() * TAU;
        s.swaySpeed = 0.05 + Math.random() * 0.15;
        s.paletteT = Math.random();
        s.driftVx = (Math.random() - 0.5) * 0.0005;
      } else {
        s.active = false;
      }
    }
  }

  _randomizeStar(star, w, h, fromRight) {
    const layerDef = star.layer === 0 ? STAR_LAYERS.FAR : star.layer === 1 ? STAR_LAYERS.MID : STAR_LAYERS.NEAR;
    star.x = fromRight ? w + Math.random() * 50 : Math.random() * w;
    star.y = Math.random() * h;
    star.baseSize = layerDef.sizeMin + Math.random() * (layerDef.sizeMax - layerDef.sizeMin);
    star.phase = Math.random() * TAU;
    star.twinkleSpeed = 0.2 + Math.random() * 1.0;
    star.brightness = 0.3 + Math.random() * 0.7;
    star.paletteT = Math.random();
  }

  // ═══════════════════════════════════════════════
  // Palette System (from lux-pond pattern)
  // ═══════════════════════════════════════════════

  _selectPalettes() {
    const count = Math.min(this.config.paletteRichness, PALETTES.length);
    this._activePalettes = [];
    for (let i = 0; i < count; i++) {
      const idx = Math.floor((i / count) * PALETTES.length) % PALETTES.length;
      this._activePalettes.push(idx);
    }
  }

  /**
   * Get a color from the active palettes with slow hue drift.
   * @param {number} t - 0-1 interpolation across palette spectrum
   * @param {number} alpha - alpha channel 0-1
   * @returns {string} rgba color string
   */
  _getPaletteColor(t, alpha) {
    const palettes = this._activePalettes;
    if (palettes.length === 0) return `rgba(100,150,200,${alpha})`;

    const totalColors = palettes.length * 6;
    const colorIdx = ((t * totalColors + this._paletteHueOffset) % totalColors + totalColors) % totalColors;
    const palIdx = palettes[Math.floor(colorIdx / 6) % palettes.length];
    const localIdx = Math.floor(colorIdx % 6);
    const rgb = PALETTES_RGB[palIdx][localIdx];

    return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${alpha})`;
  }

  /**
   * Look up palette RGB components and write to this._rgb (zero allocation).
   * Callers read this._rgb[0], this._rgb[1], this._rgb[2] after calling.
   */
  _lookupPaletteRGB(t) {
    const palettes = this._activePalettes;
    if (palettes.length === 0) {
      this._rgb[0] = 100; this._rgb[1] = 150; this._rgb[2] = 200;
      return;
    }

    const totalColors = palettes.length * 6;
    const colorIdx = ((t * totalColors + this._paletteHueOffset) % totalColors + totalColors) % totalColors;
    const palIdx = palettes[Math.floor(colorIdx / 6) % palettes.length];
    const localIdx = Math.floor(colorIdx % 6);
    const rgb = PALETTES_RGB[palIdx][localIdx];

    this._rgb[0] = rgb[0];
    this._rgb[1] = rgb[1];
    this._rgb[2] = rgb[2];
  }

  /**
   * Cap alpha by highlight ceiling and safe mode.
   */
  _clampBrightness(alpha) {
    const ceil = this.config.safeMode
      ? Math.min(this.config.highlightCeiling, 0.5)
      : this.config.highlightCeiling;
    return Math.min(ceil, Math.max(0, alpha));
  }

  // ═══════════════════════════════════════════════
  // Delta Clamping (from mandala-bloom pattern)
  // ═══════════════════════════════════════════════

  _clampDelta(current, target, maxDelta) {
    const delta = target - current;
    if (Math.abs(delta) <= maxDelta) return target;
    return current + Math.sign(delta) * maxDelta;
  }

  // ═══════════════════════════════════════════════
  // Config Change
  // ═══════════════════════════════════════════════

  onConfigChange(changed) {
    if ('starsFar' in changed || 'starsMid' in changed || 'starsNear' in changed) {
      this._activateStars();
    }
    if ('dustCount' in changed) {
      this._activateDust();
    }
    if ('shaftCount' in changed) {
      this._activateShafts();
    }
    if ('paletteRichness' in changed) {
      this._selectPalettes();
    }
  }

  // ═══════════════════════════════════════════════
  // Trail Canvas Infrastructure
  // ═══════════════════════════════════════════════

  _ensureTrailCanvas() {
    const { canvas } = this;
    if (!this._trailCanvas || this._trailCanvas.width !== canvas.width || this._trailCanvas.height !== canvas.height) {
      if (typeof OffscreenCanvas !== 'undefined') {
        this._trailCanvas = new OffscreenCanvas(canvas.width, canvas.height);
      } else {
        this._trailCanvas = document.createElement('canvas');
        this._trailCanvas.width = canvas.width;
        this._trailCanvas.height = canvas.height;
      }
      this._trailCtx = this._trailCanvas.getContext('2d');
    }
  }

  // ═══════════════════════════════════════════════
  // draw() Override — Trail Fade Technique
  // ═══════════════════════════════════════════════

  /**
   * Override draw() to produce trail fade that respects the background controller.
   *
   * Pattern: snapshot previous frame → clearCanvas() for fresh background →
   * composite snapshot at reduced opacity → draw non-trail layers → renderFrame.
   */
  draw(frequencyData, features = null) {
    const elementCount = this.getElementCount();

    if (!this._freqBuffer || this._freqBuffer.length !== elementCount) {
      this._freqBuffer = new Array(elementCount).fill(0);
    }

    const raw = mapFrequencyData(frequencyData, elementCount, this._freqBuffer);

    if (this.previousAmplitudes.length !== elementCount) {
      this.previousAmplitudes = new Array(elementCount).fill(0);
    }

    const amplitudes = smoothAmplitudes(raw, this.previousAmplitudes, this.config.smoothing);
    this._currentFeatures = features;

    const { ctx, canvas } = this;

    if (this.isFirstFrame) {
      this.clearCanvas();
      this.isFirstFrame = false;
    } else {
      this._ensureTrailCanvas();

      // 1. Snapshot current canvas (previous frame) into offscreen buffer
      this._trailCtx.clearRect(0, 0, canvas.width, canvas.height);
      this._trailCtx.drawImage(canvas, 0, 0);

      // 2. Draw fresh background (respects background controller)
      this.clearCanvas();

      // 3. Composite previous frame on top at reduced opacity
      ctx.save();
      ctx.globalAlpha = 1 - this.config.trailAlpha;
      ctx.drawImage(this._trailCanvas, 0, 0);
      ctx.restore();
    }

    // Background gradient + vignette drawn OUTSIDE trail composite
    // so they never accumulate opacity across frames
    this._drawBackgroundGradient(canvas.width, canvas.height);
    this._drawVignette(canvas.width, canvas.height);

    this.renderFrame(amplitudes, features);
  }

  // ═══════════════════════════════════════════════
  // Main Render Frame
  // ═══════════════════════════════════════════════

  renderFrame(amplitudes, features) {
    const { ctx, canvas, config } = this;
    const dt = 1 / 60;
    this.time += dt;
    const w = canvas.width;
    const h = canvas.height;

    // Slow palette hue drift
    this._paletteHueOffset += dt * config.hueDriftSpeed;

    // ── Compute band energies from amplitude array ──
    const quarter = Math.floor(amplitudes.length / 4);
    let subBassSum = 0, bassSum = 0, midSum = 0, highSum = 0;
    for (let i = 0; i < quarter; i++) subBassSum += amplitudes[i];
    for (let i = quarter; i < quarter * 2; i++) bassSum += amplitudes[i];
    for (let i = quarter * 2; i < quarter * 3; i++) midSum += amplitudes[i];
    for (let i = quarter * 3; i < amplitudes.length; i++) highSum += amplitudes[i];
    const bassE = (subBassSum + bassSum) / (quarter * 2);
    const midE = midSum / quarter;
    const highE = highSum / (amplitudes.length - quarter * 3);
    const overallE = (bassE + midE + highE) / 3;

    // ── Per-frame delta clamping for smooth audio response ──
    const maxDelta = config.safeMode ? 0.01 : 0.02;
    this._smoothedBass = this._clampDelta(this._smoothedBass, bassE, maxDelta);
    this._smoothedMids = this._clampDelta(this._smoothedMids, midE, maxDelta);
    this._smoothedTreble = this._clampDelta(this._smoothedTreble, highE, maxDelta);
    this._smoothedEnergy = this._clampDelta(this._smoothedEnergy, overallE, maxDelta * 0.5);

    // Apply calmness dampening
    const calm = config.calmness;
    const bass = this._smoothedBass * (1 - calm * 0.6);
    const mids = this._smoothedMids * (1 - calm * 0.5);
    const treble = this._smoothedTreble * (1 - calm * 0.4);
    const energy = this._smoothedEnergy * (1 - calm * 0.5);

    // ── Audio-driven modulations ──
    const driftMultiplier = 0.8 + mids * 0.7;                          // 0.8x to 1.5x
    const twinkleMod = 0.5 + treble * 1.0;                             // 0.5x to 1.5x
    const shaftIntensityBoost = bass * 0.4;                             // up to +40%
    const dustAlphaBoost = bass * 0.25;                                 // up to +25%

    // ── Energy rise detector for comet spawning ──
    const energyDelta = overallE - this._lastEnergy;
    this._lastEnergy = overallE;
    if (energyDelta > 0) {
      this._energyIntegrator += energyDelta * dt * 10;
    }
    this._energyIntegrator *= 0.95; // decay

    if (config.cometRate > 0 && this._energyIntegrator > this._energyThreshold) {
      this._energyIntegrator = 0;
      this._spawnComet(w, h);
    }

    // ── Render layers 3-9 (inside trail composite) ──

    // Layer 3: Far stars
    this._drawFarStars(w, h, dt, driftMultiplier, twinkleMod);

    // Layer 4: Mid stars
    this._drawMidStars(w, h, dt, driftMultiplier, twinkleMod);

    // Layer 5: Dust clouds
    this._drawDust(w, h, dt, driftMultiplier, dustAlphaBoost);

    // Layer 6: Light shafts
    this._drawShafts(w, h, dt, shaftIntensityBoost);

    // Layer 7: Near bokeh stars
    this._drawNearStars(w, h, dt, driftMultiplier, twinkleMod);

    // Layer 8: Color wash
    this._drawColorWash(w, h, dt);

    // Layer 9: Comets
    this._drawComets(w, h, dt);
  }

  // ═══════════════════════════════════════════════
  // Layer 2: Background Gradient + Vignette
  // (drawn OUTSIDE trail composite in draw())
  // ═══════════════════════════════════════════════

  _drawBackgroundGradient(w, h) {
    const { ctx } = this;
    const cx = w / 2;
    const cy = h / 2;

    // Slowly shifting deep-space radial gradient
    const t1 = Math.sin(this.time * 0.08) * 0.5 + 0.5;
    const t2 = Math.cos(this.time * 0.06) * 0.5 + 0.5;

    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h) * 0.7);
    grad.addColorStop(0, this._getPaletteColor(t1, 0.06));
    grad.addColorStop(0.5, this._getPaletteColor(t2 * 0.5, 0.03));
    grad.addColorStop(1, 'rgba(0,0,0,0)');

    ctx.save();
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }

  _drawVignette(w, h) {
    // Rebuild cached vignette canvas on resize
    if (this._vigW !== w || this._vigH !== h || !this._vignetteCanvas) {
      if (typeof OffscreenCanvas !== 'undefined') {
        this._vignetteCanvas = new OffscreenCanvas(w, h);
      } else {
        this._vignetteCanvas = document.createElement('canvas');
        this._vignetteCanvas.width = w;
        this._vignetteCanvas.height = h;
      }
      const vctx = this._vignetteCanvas.getContext('2d');
      const cx = w / 2;
      const cy = h / 2;
      const radius = Math.max(w, h) * 0.7;
      const grad = vctx.createRadialGradient(cx, cy, radius * 0.3, cx, cy, radius);
      grad.addColorStop(0, 'rgba(0,0,0,0)');
      grad.addColorStop(1, 'rgba(0,0,0,1)');
      vctx.fillStyle = grad;
      vctx.fillRect(0, 0, w, h);

      this._vigW = w;
      this._vigH = h;
    }

    const { ctx } = this;
    ctx.save();
    ctx.globalAlpha = 0.4;
    ctx.drawImage(this._vignetteCanvas, 0, 0);
    ctx.restore();
  }

  // ═══════════════════════════════════════════════
  // Layer 3: Far Stars (fillRect — cheapest)
  // ═══════════════════════════════════════════════

  _drawFarStars(w, h, dt, driftMul, twinkleMod) {
    const { ctx, config } = this;
    const end = MAX_STARS_FAR;

    ctx.save();

    for (let i = 0; i < end; i++) {
      const star = this._stars[i];
      if (!star.active) continue;

      // Drift leftward
      star.x -= driftMul * STAR_LAYERS.FAR.speed * dt * 30;
      star.y += Math.sin(this.time * 0.2 + star.phase) * 0.02;

      // Wrap
      if (star.x < -5) { this._randomizeStar(star, w, h, true); }
      if (star.y < -5) star.y = h + 3;
      if (star.y > h + 5) star.y = -3;

      // Twinkle
      const twinkle = Math.sin(this.time * star.twinkleSpeed / config.twinklePeriod + star.phase);
      const alpha = this._clampBrightness(
        star.brightness * 0.5 + twinkle * config.twinkleStrength * twinkleMod * 0.3
      );
      if (alpha < 0.02) continue;

      this._lookupPaletteRGB(star.paletteT);
      ctx.fillStyle = `rgba(${this._rgb[0]},${this._rgb[1]},${this._rgb[2]},${alpha.toFixed(3)})`;
      ctx.fillRect(star.x, star.y, star.baseSize, star.baseSize);
    }

    ctx.restore();
  }

  // ═══════════════════════════════════════════════
  // Layer 4: Mid Stars (arc — medium cost)
  // ═══════════════════════════════════════════════

  _drawMidStars(w, h, dt, driftMul, twinkleMod) {
    const { ctx, config } = this;
    const start = MAX_STARS_FAR;
    const end = MAX_STARS_FAR + MAX_STARS_MID;
    const glowOn = config.glowIntensity > 0;

    ctx.save();

    for (let i = start; i < end; i++) {
      const star = this._stars[i];
      if (!star.active) continue;

      // Drift
      star.x -= driftMul * STAR_LAYERS.MID.speed * dt * 30;
      star.y += Math.sin(this.time * 0.15 + star.phase) * 0.04;

      // Wrap
      if (star.x < -10) { this._randomizeStar(star, w, h, true); }
      if (star.y < -5) star.y = h + 3;
      if (star.y > h + 5) star.y = -3;

      // Twinkle
      const twinkle = Math.sin(this.time * star.twinkleSpeed / config.twinklePeriod + star.phase);
      const alpha = this._clampBrightness(
        star.brightness * 0.6 + twinkle * config.twinkleStrength * twinkleMod * 0.25
      );
      if (alpha < 0.02) continue;

      this._lookupPaletteRGB(star.paletteT);
      const r = this._rgb[0], g = this._rgb[1], b = this._rgb[2];
      const colorStr = `rgba(${r},${g},${b},${alpha.toFixed(3)})`;

      if (glowOn && star.baseSize >= 1.2) {
        ctx.shadowColor = `rgba(${r},${g},${b},${(alpha * 0.4).toFixed(3)})`;
        ctx.shadowBlur = config.glowIntensity * (star.baseSize / 3);
      }

      ctx.fillStyle = colorStr;
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.baseSize, 0, TAU);
      ctx.fill();

      if (glowOn && star.baseSize >= 1.2) {
        ctx.shadowBlur = 0;
      }
    }

    ctx.restore();
  }

  // ═══════════════════════════════════════════════
  // Layer 5: Dust Clouds
  // ═══════════════════════════════════════════════

  _drawDust(w, h, dt, driftMul, dustAlphaBoost) {
    const { ctx, config } = this;
    const minDim = Math.min(w, h);

    ctx.save();

    for (let i = 0; i < MAX_DUST; i++) {
      const d = this._dust[i];
      if (!d.active) continue;

      const cls = DUST_CLASSES[d.scaleClass];

      // Slow drift + breathing oscillation
      d.x += d.driftVx * driftMul * dt;
      d.y += d.driftVy * driftMul * dt;

      // Wrap around
      if (d.x < -0.2) d.x = 1.2;
      if (d.x > 1.2) d.x = -0.2;
      if (d.y < -0.2) d.y = 1.2;
      if (d.y > 1.2) d.y = -0.2;

      // Breathing modulation on radius
      const breathFactor = 1 + Math.sin(this.time * d.breathSpeed + d.phase) * 0.15;
      const px = d.x * w;
      const py = d.y * h;
      const radius = d.radius * minDim * breathFactor;

      // Alpha: base + bass boost
      const alpha = this._clampBrightness(cls.alphaBase + dustAlphaBoost);
      if (alpha < 0.003) continue;

      this._lookupPaletteRGB(d.paletteT + this.time * 0.005);
      const r = this._rgb[0], g = this._rgb[1], b = this._rgb[2];

      if (cls.stroked) {
        // Stroked wisp: arc path
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = `rgb(${r},${g},${b})`;
        ctx.lineWidth = 1;
        const startAngle = this.time * 0.1 + d.phase;
        ctx.beginPath();
        ctx.arc(px, py, radius, startAngle, startAngle + Math.PI * 1.2);
        ctx.stroke();
      } else {
        // Filled radial gradient blob
        const grad = ctx.createRadialGradient(px, py, 0, px, py, radius);
        grad.addColorStop(0, `rgba(${r},${g},${b},${alpha.toFixed(4)})`);
        grad.addColorStop(0.4, `rgba(${r},${g},${b},${(alpha * 0.6).toFixed(4)})`);
        grad.addColorStop(1, 'rgba(0,0,0,0)');

        ctx.globalAlpha = 1;
        ctx.fillStyle = grad;
        ctx.fillRect(px - radius, py - radius, radius * 2, radius * 2);
      }
    }

    ctx.restore();
  }

  // ═══════════════════════════════════════════════
  // Layer 6: Light Shafts / God Rays
  // ═══════════════════════════════════════════════

  _drawShafts(w, h, dt, shaftIntensityBoost) {
    const { ctx } = this;

    ctx.save();

    for (let i = 0; i < MAX_SHAFTS; i++) {
      const s = this._shafts[i];
      if (!s.active) continue;

      const cls = SHAFT_CLASSES[s.brightnessClass];

      // Very slow horizontal drift + sway
      s.x += s.driftVx * dt;
      const swayOffset = Math.sin(this.time * s.swaySpeed + s.phase) * 0.02;
      const px = (s.x + swayOffset) * w;

      // Wrap
      if (s.x < -0.1) s.x = 1.1;
      if (s.x > 1.1) s.x = -0.1;

      const shaftW = s.width;
      const shaftH = s.height * h;

      // Alpha: base + bass intensity boost
      const alpha = this._clampBrightness(cls.alphaBase + shaftIntensityBoost);
      if (alpha < 0.003) continue;

      this._lookupPaletteRGB(s.paletteT + this.time * 0.01);
      const r = this._rgb[0], g = this._rgb[1], b = this._rgb[2];

      // Vertical linear gradient: bright at top, fading down
      const grad = ctx.createLinearGradient(px, 0, px, shaftH);
      grad.addColorStop(0, `rgba(${r},${g},${b},${alpha.toFixed(4)})`);
      grad.addColorStop(0.3, `rgba(${r},${g},${b},${(alpha * 0.6).toFixed(4)})`);
      grad.addColorStop(0.7, `rgba(${r},${g},${b},${(alpha * 0.2).toFixed(4)})`);
      grad.addColorStop(1, 'rgba(0,0,0,0)');

      ctx.globalAlpha = 1;
      ctx.fillStyle = grad;
      ctx.fillRect(px - shaftW / 2, 0, shaftW, shaftH);
    }

    ctx.restore();
  }

  // ═══════════════════════════════════════════════
  // Layer 7: Near Bokeh Stars (radial gradient fill)
  // ═══════════════════════════════════════════════

  _drawNearStars(w, h, dt, driftMul, twinkleMod) {
    const { ctx, config } = this;
    const start = MAX_STARS_FAR + MAX_STARS_MID;
    const end = MAX_STARS;

    ctx.save();

    for (let i = start; i < end; i++) {
      const star = this._stars[i];
      if (!star.active) continue;

      // Fastest drift plane
      star.x -= driftMul * STAR_LAYERS.NEAR.speed * dt * 30;
      star.y += Math.sin(this.time * 0.1 + star.phase) * 0.06;

      // Wrap
      if (star.x < -20) { this._randomizeStar(star, w, h, true); }
      if (star.y < -10) star.y = h + 5;
      if (star.y > h + 10) star.y = -5;

      // Gentle twinkle
      const twinkle = Math.sin(this.time * star.twinkleSpeed * 0.5 + star.phase);
      const alpha = this._clampBrightness(
        star.brightness * 0.4 + twinkle * config.twinkleStrength * twinkleMod * 0.15
      );
      if (alpha < 0.02) continue;

      const size = star.baseSize;
      this._lookupPaletteRGB(star.paletteT);
      const r = this._rgb[0], g = this._rgb[1], b = this._rgb[2];

      // Radial gradient for soft bokeh effect
      const grad = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, size);
      grad.addColorStop(0, `rgba(${r},${g},${b},${alpha.toFixed(3)})`);
      grad.addColorStop(0.3, `rgba(${r},${g},${b},${(alpha * 0.6).toFixed(3)})`);
      grad.addColorStop(0.7, `rgba(${r},${g},${b},${(alpha * 0.2).toFixed(3)})`);
      grad.addColorStop(1, 'rgba(0,0,0,0)');

      ctx.globalAlpha = 1;
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(star.x, star.y, size, 0, TAU);
      ctx.fill();
    }

    ctx.restore();
  }

  // ═══════════════════════════════════════════════
  // Layer 8: Color Wash
  // ═══════════════════════════════════════════════

  _drawColorWash(w, h, dt) {
    const { ctx } = this;

    // Very slow rotation
    this._washAngle += dt * 0.015;

    const cx = w / 2;
    const cy = h / 2;
    const radius = Math.max(w, h) * 0.8;

    // Two palette colors at very low alpha
    const t1 = (Math.sin(this.time * 0.04) * 0.5 + 0.5);
    const t2 = (Math.cos(this.time * 0.03) * 0.5 + 0.5);

    const x1 = cx + Math.cos(this._washAngle) * radius;
    const y1 = cy + Math.sin(this._washAngle) * radius;
    const x2 = cx - Math.cos(this._washAngle) * radius;
    const y2 = cy - Math.sin(this._washAngle) * radius;

    const grad = ctx.createLinearGradient(x1, y1, x2, y2);
    grad.addColorStop(0, this._getPaletteColor(t1, 0.035));
    grad.addColorStop(0.5, 'rgba(0,0,0,0)');
    grad.addColorStop(1, this._getPaletteColor(t2, 0.035));

    ctx.save();
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }

  // ═══════════════════════════════════════════════
  // Layer 9: Comets
  // ═══════════════════════════════════════════════

  _spawnComet(w, h) {
    if (this.config.cometRate <= 0) return;

    // Find an inactive comet slot
    let slot = -1;
    for (let i = 0; i < MAX_COMETS; i++) {
      const idx = (this._cometNextIdx + i) % MAX_COMETS;
      if (!this._comets[idx].active) {
        slot = idx;
        break;
      }
    }
    // If no inactive slot, recycle the oldest
    if (slot === -1) {
      slot = this._cometNextIdx;
    }
    this._cometNextIdx = (slot + 1) % MAX_COMETS;

    const c = this._comets[slot];
    c.active = true;
    // Start from right side or top, traveling left-downward
    c.x = w * (0.5 + Math.random() * 0.5);
    c.y = Math.random() * h * 0.4;
    c.vx = -(15 + Math.random() * 25);  // slow leftward
    c.vy = 5 + Math.random() * 15;       // gentle downward
    c.life = 2 + Math.random() * 3;       // 2-5 seconds
    c.maxLife = c.life;
    c.length = 30 + Math.random() * 60;
    c.paletteT = Math.random();
  }

  _drawComets(w, h, dt) {
    const { ctx, config } = this;

    ctx.save();

    for (let i = 0; i < MAX_COMETS; i++) {
      const c = this._comets[i];
      if (!c.active) continue;

      c.life -= dt;
      if (c.life <= 0) {
        c.active = false;
        continue;
      }

      // Move
      c.x += c.vx * dt;
      c.y += c.vy * dt;

      // Off-screen check
      if (c.x < -100 || c.x > w + 100 || c.y > h + 100) {
        c.active = false;
        continue;
      }

      // Fade-in/sustain/fade-out envelope
      const lifeFrac = c.life / c.maxLife;
      let envelope;
      if (lifeFrac > 0.8) {
        envelope = (1 - lifeFrac) * 5; // fade in over first 20%
      } else if (lifeFrac < 0.2) {
        envelope = lifeFrac * 5;        // fade out over last 20%
      } else {
        envelope = 1;                    // sustain
      }

      const alpha = this._clampBrightness(0.4 * envelope);
      if (alpha < 0.01) continue;

      this._lookupPaletteRGB(c.paletteT);
      const r = this._rgb[0], g = this._rgb[1], b = this._rgb[2];

      // Comet trail: line from current position backward along velocity
      const speed = Math.sqrt(c.vx * c.vx + c.vy * c.vy);
      const nx = -c.vx / speed;
      const ny = -c.vy / speed;
      const tailX = c.x + nx * c.length;
      const tailY = c.y + ny * c.length;

      // Gradient stroke from head (bright) to tail (transparent)
      const grad = ctx.createLinearGradient(c.x, c.y, tailX, tailY);
      grad.addColorStop(0, `rgba(${r},${g},${b},${alpha.toFixed(3)})`);
      grad.addColorStop(0.4, `rgba(${r},${g},${b},${(alpha * 0.4).toFixed(3)})`);
      grad.addColorStop(1, 'rgba(0,0,0,0)');

      // Glow on comet head
      if (config.glowIntensity > 0) {
        ctx.shadowColor = `rgba(${r},${g},${b},${(alpha * 0.6).toFixed(3)})`;
        ctx.shadowBlur = config.glowIntensity * 0.8;
      }

      ctx.strokeStyle = grad;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(c.x, c.y);
      ctx.lineTo(tailX, tailY);
      ctx.stroke();

      ctx.shadowBlur = 0;

      // Bright head dot
      ctx.fillStyle = `rgba(${Math.min(255, r + 60)},${Math.min(255, g + 60)},${Math.min(255, b + 60)},${(alpha * 0.8).toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(c.x, c.y, 1.5, 0, TAU);
      ctx.fill();
    }

    ctx.restore();
  }

  // ═══════════════════════════════════════════════
  // Clear / Cleanup
  // ═══════════════════════════════════════════════

  clear() {
    super.clear();
    this.time = 0;
    this.isFirstFrame = true;
    this._smoothedBass = 0;
    this._smoothedMids = 0;
    this._smoothedTreble = 0;
    this._smoothedEnergy = 0;
    this._energyIntegrator = 0;
    this._lastEnergy = 0;
    this._paletteHueOffset = 0;
    this._washAngle = 0;

    // Reset star pool
    for (let i = 0; i < MAX_STARS; i++) {
      this._stars[i].active = false;
    }

    // Reset dust pool
    for (let i = 0; i < MAX_DUST; i++) {
      this._dust[i].active = false;
    }

    // Reset shaft pool
    for (let i = 0; i < MAX_SHAFTS; i++) {
      this._shafts[i].active = false;
    }

    // Reset comet pool
    for (let i = 0; i < MAX_COMETS; i++) {
      this._comets[i].active = false;
    }
    this._cometNextIdx = 0;

    // Clear trail buffer
    if (this._trailCtx && this._trailCanvas) {
      this._trailCtx.clearRect(0, 0, this._trailCanvas.width, this._trailCanvas.height);
    }

    // Invalidate vignette cache
    this._vignetteCanvas = null;

    // Re-activate initial elements
    this._activateStars();
    this._activateDust();
    this._activateShafts();
  }
}

// Register with the visualizer registry
VisualizerRegistry.register(DeepStarCathedral);

export { DeepStarCathedral };
export default DeepStarCathedral;
